import React, { useState, useEffect } from "react";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import {
  useForm,
  SubmitHandler,
  Controller,
  useWatch,
} from "react-hook-form";
import { StylesConfig } from "react-select";
import DurationSelect from "app/components/inputs/DurationSelect";
import styles from "./styles.module.scss";
import CloseIcon from "../../../../../../public/images/close.svg";
import { useTranslation } from "react-i18next";
import TimeTableSelector, {
  TimeTable,
} from "app/routes/Scheduling/Pricing/TimeTableSelector";
import ButtonVariant from "app/components/Button/ButtonVariant";
import TextInput from "app/components/inputs/TextInput";
import CheckBox from "app/components/inputs/CheckBox";
import ApplyScopeConfirmModal from "app/components/ApplyScopeConfirmModal";
import { parseDurationMinutes } from "app/utils/timeUtils";
import PayMethodSelector, {
  PaymentMethodValue,
  DEFAULT_PAYMENT_VALUE,
  toPaymentPayload,
  fromPaymentModel,
} from "app/components/PayMethodSelector";
import useSpaceTemplates from "app/routes/Scheduling/Pricing/useSpaceTemplates";
import SpaceTemplateSelector from "./SpaceTemplateSelector";
import { resourceIdsPayload } from "app/components/ResourceSelector";
import ResourcesBlock from "app/components/ResourceSelector/ResourcesBlock";
import {
  ScheduleResourceResponse,
  ResourceCategoryResponse,
} from "app/utils/request/scheduling/facilityResources";
import {
  ResourceSelection,
  EMPTY_RESOURCE_SELECTION,
} from "app/components/ResourceSelector/selection";
import Section from "./Section";
import CalendarColorPicker from "app/components/inputs/CalendarColorPicker";
import { toPayloadColor } from "app/utils/calendarColors";


interface FormValues {
  id: string | number;
  rate: number | string;
  defaultRate: boolean;
  location: string;
  capacity: number | string;
  duration: string;
  title: string;
  _destroy?: boolean;
  active?: boolean;
  paymentMethod?: PaymentMethodValue;
  restrictGroup?: string;
  timeTables?: any[];
  bookingsLimit?: number | string | null;
  bookingOptionReminder?: string;
  description?: string;
  hidePrice?: boolean;
  spaceTemplateId?: string | null;
  color?: string | null;
  [key: string]: any;
}

interface EditPricingProps {
  isOpen: boolean;
  onClose: () => void;
  item?: FormValues;
  onEdit: (_updatedItem: FormValues) => void;
  titles: string[];
  coachCurrencySymbol: string;
  timeTables: TimeTable[];
  showBookingsLimit?: boolean;
  stripeIntegrationEnabled?: boolean;
  resources?: ScheduleResourceResponse[];
  categories?: ResourceCategoryResponse[];
  resourcesEnabled?: boolean;
}

export default function EditPricing({
  isOpen,
  onClose,
  item,
  onEdit,
  titles,
  coachCurrencySymbol,
  timeTables,
  showBookingsLimit = false,
  stripeIntegrationEnabled,
  resources = [],
  categories = [],
  resourcesEnabled = false,
}: EditPricingProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    control,
    trigger,
  } = useForm<FormValues>();

  const [titleError, setTitleError] = useState<string | null>(null);
  const [selectedAvailabilities, setSelectedAvailabilities] = useState(
    item?.timeTables?.map((tt: any) => tt.id)
  );
  const { t } = useTranslation();

  // CN-2711: hiding the price only makes sense when the athlete never reaches
  // a checkout, so the option has to be collected offline and nothing else.
  const paymentMethod = useWatch({ control, name: "paymentMethod" });
  const hidePrice = useWatch({ control, name: "hidePrice" });
  const canHidePrice = (paymentMethod?.collectType ?? "offline") === "offline";

  useEffect(() => {
    if (!canHidePrice && hidePrice) {
      setValue("hidePrice", false);
    }
  }, [canHidePrice, hidePrice, setValue]);
  const [showPayments, setShowPayments] = useState(false);
  const [showOther, setShowOther] = useState(false);
  // The modal stays mounted, and the availability card keeps its own folded
  // state. Bumping this on the way out remounts it folded, so reopening the
  // modal never starts on a card the last edit happened to leave open
  // (CFTB-3523).
  const [sectionsKey, setSectionsKey] = useState(0);
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [resourceSelection, setResourceSelection] = useState<ResourceSelection>(
    EMPTY_RESOURCE_SELECTION
  );
  const [pendingData, setPendingData] = useState<FormValues | null>(null);
  const [showApplyScope, setShowApplyScope] = useState(false);
  const { templates: spaceTemplates } = useSpaceTemplates();

  // Pre-fill form when item changes
  useEffect(() => {
    let isMounted = true;

    if (item && isMounted) {
      setValue("id", item.id);
      setValue("title", item.title);
      setValue("duration", item.duration);
      setValue("rate", item.rate || "");
      setValue("location", item.location);
      setValue("capacity", item.capacity || 1);
      setValue("defaultRate", item.defaultRate);
      setValue("restrictGroup", item.restrictGroup || "");
      setValue("bookingsLimit", item.bookingsLimit ?? "");
      setValue("bookingOptionReminder", item.bookingOptionReminder || "");
      setValue("description", item.description || "");
      setValue("hidePrice", !!item.hidePrice);
      setValue("spaceTemplateId", item.spaceTemplateId || null);
      setValue("color", item.color ?? null);
      setValue(
        "paymentMethod",
        fromPaymentModel({
          collectType: (item as any).collectType,
          onlineDefault: (item as any).onlineDefault,
          packageOnly: (item as any).packageOnly,
        })
      );
    }

    return () => {
      isMounted = false;
    };
  }, [item, setValue]);

  // Reload the picker from the saved item on each open. A Cancel keeps the
  // unsaved pick in state, so a reopen must not show it.
  useEffect(() => {
    if (item && isOpen) {
      setResourceSelection({
        scheduleResourceIds: item.scheduleResourceIds ?? [],
        resourceCategoryIds: item.resourceCategoryIds ?? [],
      });
    }
  }, [item, isOpen]);

  // Drop transient apply-scope state whenever the edit modal closes, so a
  // dismissed edit never leaks its confirmation modal or a stale payload into
  // the next open.
  useEffect(() => {
    if (!isOpen) {
      setShowApplyScope(false);
      setPendingData(null);
    }
  }, [isOpen]);

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    setCapacityError(null);

    if (
      item &&
      data.title !== item.title &&
      Array.isArray(titles) &&
      titles.includes(data.title)
    ) {
      setTitleError(t("Title must be unique"));
      return;
    }

    const capacityVal = data.capacity !== "" ? Number(data.capacity) : 1;
    if (capacityVal < 1) {
      setCapacityError(t("Must be at least 1"));
      return;
    }

    const { collect_type, online_default } = toPaymentPayload(
      data.paymentMethod
    );

    // CN-2711: the request layer snake-cases whatever it is handed, so leaving
    // hidePrice in the spread would put hide_price in every payload — exactly
    // what the conditional below is avoiding. Drop it here and add it back only
    // when it changes something. `color` rides out for the same reason.
    const { hidePrice: hidePriceValue, color: colorValue, ...formValues } = data;
    const pickedColor = toPayloadColor(colorValue);

    const formattedData = {
      ...formValues,
      rate: data.rate !== "" ? data.rate : 0,
      active: item.active,
      timeTableIds: selectedAvailabilities,
      collect_type,
      online_default,
      bookings_limit:
        data.bookingsLimit !== "" ? Number(data.bookingsLimit) : null,
      booking_option_reminder: data.bookingOptionReminder?.trim() || null,
      description: data.description?.trim() || null,
      space_template_id: data.spaceTemplateId || null,
      // CN-2711: only send the flag when it actually asks for something. A
      // booking service that predates the column answers 500 to any payload
      // carrying hide_price, so an unrelated edit must not start failing
      // against an environment where this feature has not shipped yet.
      ...(!!hidePriceValue !== !!item?.hidePrice ? { hide_price: !!hidePriceValue } : {}),
      // Calendar colour coding takes the same guard, keyed on "changed" so
      // clearing a colour still sends the null that unsets it. Compared raw:
      // a legacy value outside the palette shows as None, and normalizing it
      // first would clear it on every unrelated save.
      ...((colorValue ?? null) !== (item?.color ?? null) ? { color: pickedColor } : {}),
      ...resourceIdsPayload(
        resourcesEnabled,
        resourceSelection.scheduleResourceIds,
        resourceSelection.resourceCategoryIds
      ),
    };

    // Only ask about existing bookings when duration/location changed.
    if (durationOrLocationChanged(data)) {
      setPendingData(formattedData);
      setShowApplyScope(true);
      return;
    }

    submitEdit(formattedData, false);
  };

  const durationOrLocationChanged = (data: FormValues) => {
    if (!item) {
      return false;
    }
    const before = parseDurationMinutes(item.duration);
    const after = parseDurationMinutes(data.duration);
    const durationChanged =
      before !== null && after !== null
        ? before !== after
        : data.duration !== item.duration;
    return durationChanged || (data.location || "") !== (item.location || "");
  };

  const submitEdit = (formattedData: any, applyToExisting: boolean) => {
    onEdit({ ...formattedData, apply_to_existing: applyToExisting });
    onClose();
    reset({
      id: undefined,
      title: "",
      duration: "",
      rate: 0,
      location: "",
      capacity: 1,
      defaultRate: false,
      paymentMethod: DEFAULT_PAYMENT_VALUE,
      bookingsLimit: "",
      bookingOptionReminder: "",
      description: "",
      hidePrice: false,
      spaceTemplateId: null,
      color: null,
    });
    setTitleError("");
    setShowApplyScope(false);
    setPendingData(null);
    // The modal stays mounted, so the cards it was left on would greet the next
    // booking option opened -- folded is where they start.
    setShowPayments(false);
    setShowOther(false);
    setSectionsKey((key) => key + 1);
  };

  // Leaving by Cancel folds the cards the same way saving does, so the next
  // booking option opened never inherits how this one was left (CFTB-3523).
  const onCancel = () => {
    onClose();
    setShowPayments(false);
    setShowOther(false);
    setSectionsKey((key) => key + 1);
  };

  const customStyles: StylesConfig = {
    control: (base: any) => ({
      ...base,
      background: "white",
      border: "1px solid #ccc",
    }),
    multiValue: (styles: any) => ({
      ...styles,
      backgroundColor: "#27a4ff",
      padding: "4px",
      borderRadius: "4px",
    }),
    multiValueLabel: (styles: any) => ({
      ...styles,
      color: "white",
      fontSize: "16px",
    }),
    multiValueRemove: (styles: any) => ({
      ...styles,
      color: "white",
      ":hover": {
        color: "white",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "black",
    }),
    container: (provided: any) => ({
      ...provided,
      color: "#8C8C8C",
    }),
    placeholder: (defaultStyles: any) => ({
      ...defaultStyles,
      color: "#9faac7",
    }),
  };

  const durationCustomStyles: StylesConfig = {
    ...customStyles,
    valueContainer: (base: any) => ({
      ...base,
      height: "42px",
      display: "flex",
      alignItems: "center",
    }),
  };

  useEffect(() => {
    register("duration", { required: true });
  }, [register]);

  return (
    <>
    <ModalLayout isOpen={isOpen} onClose={onClose} classNames={styles.modal}>
      <div className={styles.modal_header}>
        <h2>{t("Edit Booking")}</h2>
        <img
          className={styles.modal_status_icon}
          src={CloseIcon}
          alt="info"
          onClick={onClose}
        />
      </div>
      <div className={styles.modal_content}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.form_row}>
            <TextInput
              label={`${t("Title")}*`}
              placeholder={t("e.g. 1 Hour Adult Lesson")}
              {...register("title", { required: true })}
            />
            {errors.title && <span>{t("This field is required")}</span>}
            {titleError && <span>{titleError}</span>}
          </div>

          <div className={styles.form_row}>
            <label htmlFor="duration">{t("Duration")}*</label>
            <DurationSelect
              control={control}
              trigger={trigger}
              styles={durationCustomStyles}
            />
            {errors.duration && <span>{t("This field is required")}</span>}
          </div>

          {/* Price and location share a row, with the price's own
              question under it, as the designs have them. */}
          <div className={styles.form_pair}>
            <div className={styles.form_pair_col}>
            <div className={styles.form_row}>
              <label htmlFor="rate">{t("Rate")}</label>
              <div className={styles.input_with_dollar}>
                <span className={styles.dollar_sign}>{coachCurrencySymbol}</span>
                <input
                  id="rate"
                  type="number"
                  {...register("rate")}
                  placeholder="0"
                  defaultValue={item ? item.rate : ""}
                  onKeyPress={(e) => {
                    if (e.key === "-" || e.key === "e" || e.key === "+") {
                      e.preventDefault();
                    }
                  }}
                  onBlur={(e) => {
                    const value = parseFloat(e.target.value);
                    if (value < 0 || isNaN(value)) {
                      setValue("rate", 0);
                      trigger("rate");
                    }
                  }}
                />
              </div>
            </div>

            {/* CN-2711: the rate is still charged and reported, it just stops
                being shown to whoever is booking. Only offered when the option is
                collected offline only — any online payment means a checkout, and
                a checkout cannot hide what it charges. */}
            {canHidePrice && (
              <div className={styles.form_row_checkbox}>
                <Controller
                  name="hidePrice"
                  control={control}
                  render={({ field }) => (
                    <CheckBox
                      name="hidePrice"
                      label={t("Hide price?")}
                      checked={!!field.value}
                      onChange={field.onChange}
                      squareCheckIcons
                      testId="hide-price-checkbox"
                    />
                  )}
                />
              </div>
            )}
            </div>
            <div className={styles.form_pair_col}>
            <div className={styles.form_row}>
              <TextInput
              label={t("Location")}
              placeholder={t("Where the session takes place")}
              {...register("location")}
            />
            </div>
            </div>
          </div>


          <div className={styles.form_row}>
            <label htmlFor="description">
              {t("Description (shown on your landing page)")}
            </label>
            <textarea
              id="description"
              {...register("description", { maxLength: 1000 })}
              placeholder={t("What this session covers, for whoever is booking it")}
              maxLength={1000}
              rows={3}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontFamily: "inherit",
                fontSize: "inherit",
                resize: "vertical",
              }}
            />
          </div>





          <div className={styles.paymentDropdown}>
            <div
              className={styles.paymentToggle}
              onClick={() => setShowPayments(!showPayments)}
            >
              <div>
                <div className={styles.paymentsTitle}>{t("Payments")}</div>
                {!showPayments && (
                  <div className={styles.paymentSummary}>
                    {t("Online and Offline")}
                  </div>
                )}
              </div>
              <i
                className={`ico ico-chevron ${showPayments ? styles.open : ""}`}
              />
            </div>

            {showPayments && (
              <div className={styles.paymentsContent}>
                <p className={styles.subtext}>
                  {t(
                    "Please choose your preferred method for collecting payments for this booking option."
                  )}
                </p>

                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <PayMethodSelector
                      value={field.value}
                      onChange={field.onChange}
                      showPackageOnly
                      disableOnline={!stripeIntegrationEnabled}
                      disabledReason={
                        !stripeIntegrationEnabled
                          ? t("Connect Stripe to enable online payments")
                          : undefined
                      }
                    />
                  )}
                />
              </div>
            )}
          </div>

          {resourcesEnabled && (
            <ResourcesBlock
              resources={resources}
              categories={categories}
              value={resourceSelection}
              onChange={setResourceSelection}
              blockStyles={styles}
            />
          )}

          <TimeTableSelector
            key={sectionsKey}
            timeTables={timeTables}
            value={selectedAvailabilities || []}
            onChange={setSelectedAvailabilities}
          />

          {/* Set once and rarely looked at again, so the designs fold it
              away: the reminder and the template, then the two limits,
              then what the calendar draws this option as. */}
          <Section
            title={t("Other customization")}
            isOpen={showOther}
            onToggle={() => setShowOther(!showOther)}
            testId="other-customization-section-toggle"
          >
            {/* One field per row, as the designs draw this card: label, field,
                and what it is for underneath. */}
            <div className={styles.stack}>
              <div className={styles.stack_field}>
                <label htmlFor="bookingOptionReminder">{t("Reminder")}</label>
                <textarea
                  id="bookingOptionReminder"
                  {...register("bookingOptionReminder", { maxLength: 1000 })}
                  placeholder={t("What to bring, where to meet, anything else worth saying")}
                  maxLength={1000}
                  rows={3}
                />
                <span className={styles.stack_hint}>
                  {t("Included in the booking confirmation email.")}
                </span>
              </div>

              <div className={styles.stack_field}>
                <Controller
                  name="spaceTemplateId"
                  control={control}
                  render={({ field }) => (
                    <SpaceTemplateSelector
                      templates={spaceTemplates}
                      value={field.value}
                      onChange={field.onChange}
                      styles={customStyles}
                    />
                  )}
                />
              </div>

              {showBookingsLimit && (
                <div className={styles.stack_field}>
                  <label htmlFor="capacity">{t("Capacity")}</label>
                  <input
                    id="capacity"
                    type="number"
                    {...register("capacity")}
                    placeholder="1"
                    onKeyPress={(e) => {
                      if (["-", "e", "+", ".", ","].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value < 1) {
                        setCapacityError(t("Must be at least 1"));
                      } else {
                        setCapacityError(null);
                      }
                    }}
                    onChange={(e) => {
                      setCapacityError(null);
                      register("capacity").onChange(e);
                    }}
                  />
                  <span className={styles.stack_hint}>
                    {t("The maximum number of athletes who can book the same session.")}
                  </span>
                  {capacityError && (
                    <span className={styles.stack_error}>{capacityError}</span>
                  )}
                </div>
              )}

              {showBookingsLimit && (
                <div className={styles.stack_field}>
                  <label htmlFor="bookingsLimit">{t("Bookings Limit")}</label>
                  <input
                    id="bookingsLimit"
                    type="number"
                    {...register("bookingsLimit", {
                      min: { value: 1, message: t("Must be at least 1") },
                    })}
                    placeholder={t("Unlimited")}
                    onKeyPress={(e) => {
                      if (e.key === "-" || e.key === "e" || e.key === "+" || e.key === ".") {
                        e.preventDefault();
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (value < 1) {
                        setValue("bookingsLimit", "");
                        trigger("bookingsLimit");
                      }
                    }}
                  />
                  <span className={styles.stack_hint}>
                    {t("Maximum number of future sessions a student can book")}
                  </span>
                  {errors.bookingsLimit && (
                    <span className={styles.stack_error}>{errors.bookingsLimit.message}</span>
                  )}
                </div>
              )}

              <div className={styles.stack_field}>
                <Controller
                  name="color"
                  control={control}
                  render={({ field }) => (
                    <CalendarColorPicker
                      label={t("Calendar colour")}
                      hint={t("Sessions booked with this option appear in this colour on the calendar.")}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>
          </Section>

          <div className={styles.buttonGroup}>
            <ButtonVariant
              title={t("Cancel")}
              buttonType="secondary-outline"
              buttonStyles={styles.cancel_button}
              onClickFunc={onCancel}
              data-testid="edit-booking-cancel-button"
            />
            <ButtonVariant
              title={t("Confirm")}
              buttonType="primary"
              buttonStyles={styles.confirm_button}
              onClickFunc={handleSubmit(onSubmit)}
              data-testid="edit-booking-confirm-button"
            />
          </div>
        </form>
      </div>
    </ModalLayout>

      <ApplyScopeConfirmModal
        isOpen={showApplyScope}
        onAllUpcoming={() => pendingData && submitEdit(pendingData, true)}
        onNewOnly={() => pendingData && submitEdit(pendingData, false)}
        onCancel={() => {
          setShowApplyScope(false);
          setPendingData(null);
        }}
      />
    </>
  );
}
